import { useState } from 'react'
import { Delete, Edit } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OrgCredential, OrgCredentialType } from '@/types/credentials'
import useOverlay, { OverlayState } from '@/hooks/useOverlay'
import { getCredential } from '@/services/credentials'
import { useDeleteOrgCredential } from '@/service-library/hooks/org-credentials'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import ManageCredentialAuthDialog from './ManageCredentialAuthDialog'

type CredentialCardProps = {
  addEditOverlay: OverlayState
  credential: OrgCredential
  listQueryKey: QueryKey
  setSelectCredentialId: (id: string) => void
  type: OrgCredentialType
}

export default function CredentialCard({
  addEditOverlay,
  credential,
  listQueryKey,
  setSelectCredentialId,
  type,
}: CredentialCardProps) {
  const manageAuthOverlay = useOverlay()
  const [authTypeForDialog, setAuthTypeForDialog] = useState<
    'secret' | 'token' | undefined
  >(undefined)
  const [oauthUrlIsOpening, setOauthUrlIsOpening] = useState(false)

  const { deleteOrgCredential } = useDeleteOrgCredential({
    listQueryKey,
    onSuccess: () => {
      showSuccessSnackbar('Credential Deleted')
    },
    onError: () => {
      showErrorSnackbar('Failed to delete credentials. Please contact support.')
    },
  })

  function handleAuthenticate() {
    setOauthUrlIsOpening(true)
    getCredential(type.trigger_url, credential.id).then(({ data }) => {
      setOauthUrlIsOpening(false)
      window.open(data.auth_url, '_blank')
    })
  }

  return (
    <Card sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6">{credential.name}</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              onClick={() => {
                setSelectCredentialId(credential.id)
                addEditOverlay.open()
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              onClick={() => {
                deleteOrgCredential(credential.id)
              }}
              size="small"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {type?.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Organization: {credential.org?.name || ''}
      </Typography>

      <Stack
        spacing={0.5}
        sx={{
          mt: 1,
          ml: -0.5, // So that the button text aligns with the card content
        }}
      >
        {type.auth_type.map((authType) => {
          if (authType == 'secret' || authType == 'token') {
            return (
              <Button
                key={authType}
                sx={{ width: 'fit-content' }}
                variant="text"
                onClick={(e) => {
                  setAuthTypeForDialog(authType)
                  manageAuthOverlay.open(e)
                }}
              >
                {credential.needs_secrets ? 'Set' : 'Edit'}{' '}
                {authType === 'secret' ? 'Client ID and Secret' : 'API Token'}
              </Button>
            )
          }
          return (
            <LoadingButton
              loading={oauthUrlIsOpening}
              disabled={oauthUrlIsOpening}
              key={authType}
              color="secondary"
              variant="text"
              onClick={handleAuthenticate}
              sx={{ width: 'fit-content' }}
            >
              {credential.needs_auth ? 'Authenticate' : 'Re-authenticate'}
            </LoadingButton>
          )
        })}
      </Stack>
      {authTypeForDialog && (
        <ManageCredentialAuthDialog
          authType={authTypeForDialog}
          isEdit={!credential.needs_secrets}
          overlay={manageAuthOverlay}
          orgCredential={credential}
          type={type}
          onSave={() => {
            setAuthTypeForDialog(undefined)
          }}
        />
      )}
    </Card>
  )
}
